import * as React from 'react';
import {useHistory} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import logger from 'src/utils/logger';

import {UPDATE_APP_URL} from 'src/chat-extension/messages';
import {EXTENSION_ID} from 'src/chat-extension/constants';

import {validateEmail} from 'src/utils/index';

import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  TitleMedium,
  BodySmall,
  BodyMedium,
  ButtonTextMedium,
  ButtonTextSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {AuthFlow} from 'src/components/authentication/auth-flow';
import {
  mockSignIn,
  authenticateCreds,
  performSignIn,
  centralLoginAuth,
} from 'src/action-creators/authentication';

import type {SignInPageProps} from 'src/types/authentication';

import {
  selectMfaCredsValidating,
  selectMfaCredsError,
  selectSignInError,
  selectRetryInMins,
} from 'src/selectors/authentication';

import Logo from 'src/images/sense-logo.svg';
import classify from 'src/utils/classify';

import css from './sign-in.css';
import maincss from 'src/components/authentication/main.css';


const SignInPage = (props: SignInPageProps) => {
  const {t, makeKey} = useLabelTranslation();
  const dispatch = useDispatch();
  const router = useHistory();

  const signingIn = useSelector(selectMfaCredsValidating);
  const signinError = useSelector(selectMfaCredsError);
  const signInErrorText = useSelector(selectSignInError);
  const retry_login_in_mins = useSelector(selectRetryInMins);
  const retryLoginInMinsKey =
    'Too many failed attempts. Please try again in {{retry_login_in_mins}} mins.';

  const {goToPage, dynamicAgency, setCreds} = props;

  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [isValidEmail, setIsValidEmail] = React.useState<boolean>(true);
  const [isEmptyPassword, setIsEmptyPassword] = React.useState<boolean>(false);
  // @ts-expect-error - TS2339 - Property 'env' does not exist on type 'DefaultRootState'.
  const {_a, _b, hostname} = useSelector((state) => state.env);

  const validateUserEmail = () => {
    setIsValidEmail(validateEmail(email));
  };

  const getHost = () => {
    return hostname.split('.').slice(1).join('.');
  };

  const updateExtensionURL = (appUrl: string) => {
    try {
      const chrome = window?.chrome;

      if (
        !chrome ||
        !chrome.runtime ||
        typeof chrome.runtime.sendMessage !== 'function'
      ) {
        logger.log(
          'Chrome extension not available or sendMessage is undefined',
        );
        return;
      }

      const message = {
        type: UPDATE_APP_URL,
        payload: appUrl,
      } as const;

      chrome.runtime.sendMessage(EXTENSION_ID, message, (response) => {
        logger.log('app url updated', appUrl);
      });
    } catch (error: any) {
      logger.log('error updating app url', error);
    }
  };

  const handleDynamicAgencySignIn = async () => {
    const apiData = await dispatch(centralLoginAuth(email, password));
    setCreds({email, password});
    //@ts-expect-error
    if (apiData?.length > 1) {
      goToPage(AuthFlow.DYNAMIC_AGENCY.SELECT_AGENCY, 'DYNAMIC_AGENCY');
      //@ts-expect-error
    } else if (apiData?.length === 1) {
      const host = getHost();
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type 'ThunkAction<void>'.
      dispatch(mockSignIn(email, password, host, apiData[0].slug));
    }
  };

  const handleMigratedAgent = (apiData: any) => {
    if (!apiData?.responseBody) {
      return;
    }
    const host = getHost();
    const slug = apiData?.responseBody?.slug;
    const appURL = `${slug}.${host}`;
    updateExtensionURL(appURL);
    dispatch(mockSignIn(email, password, host, slug));
  };

  // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail || isEmptyPassword) {
      return;
    }

    if (dynamicAgency) {
      handleDynamicAgencySignIn();
      return;
    }

    const apiData = await dispatch(authenticateCreds(email, password));
    //@ts-expect-error
    if (!apiData) {
      return;
    }

    // @ts-expect-error - TS2339 - Property 'response' does not exist on type 'ThunkAction<void>'.
    if (apiData?.response?.status === 300) {
      handleMigratedAgent(apiData);
      return;
    }

    // @ts-expect-error - TS2339 - Property 'agency' does not exist on type 'ThunkAction<void>'.
    const mfa_enabled = apiData?.agency?.mfa_auth_channel;

    if (!mfa_enabled) {
      // @ts-expect-error - TS2339 - Property 'agency' does not exist on type 'ThunkAction<void>'.
      dispatch(performSignIn(apiData?.agency));
      return;
    }

    // @ts-expect-error - TS2339 - Property 'mfa_settings' does not exist on type 'ThunkAction<void>'.
    const authChannel = apiData?.mfa_settings?.auth_channel;
    // @ts-expect-error - TS2339 - Property 'mfa_settings' does not exist on type 'ThunkAction<void>'.
    const userPhone = apiData?.mfa_settings?.phone_number;
    // @ts-expect-error - TS2339 - Property 'mfa_settings' does not exist on type 'ThunkAction<void>'.
    const shouldShowEnrollTotp = apiData?.mfa_settings?.should_enroll_totp;

    if (authChannel === 'SMS' && userPhone) {
      goToPage(AuthFlow.SMS.OTP_INPUT, 'SMS');
    } else if (authChannel === 'SMS') {
      goToPage(
        AuthFlow.SMS_REGISTRATION.PHONE_REGISTRATION,
        'SMS_REGISTRATION',
      );
    } else if (authChannel === 'EMAIL') {
      goToPage(AuthFlow.EMAIL.OTP_INPUT, 'EMAIL');
    } else if (authChannel === 'TOTP' && userPhone) {
      if (shouldShowEnrollTotp) {
        // when only SMS is registered
        goToPage(AuthFlow.TOTP.OTP_INPUT, 'TOTP');
      } else {
        // when both TOTP and SMS are registered
        goToPage(AuthFlow.TOTP.TOTP_AND_SMS_SELECTION, 'TOTP');
      }
    } else if (authChannel === 'TOTP' && !userPhone) {
      if (!shouldShowEnrollTotp) {
        // when TOTP is registered
        goToPage(AuthFlow.TOTP.TOTP_VERIFY, 'TOTP');
      } else {
        // when both TOTP and SMS are not registered
        goToPage(AuthFlow.TOTP.TOTP_AND_SMS_REGISTRATION_SELECTION, 'TOTP');
      }
    } else if (authChannel === 'TOTP') {
      goToPage(AuthFlow.TOTP.TOTP_VERIFY, 'TOTP');
    }

    return;
  };

  const handleResetPassowrd = () => {
    router.replace('/forgot');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleEmailChange = (value: string) => {
    if (!isValidEmail) {
      setIsValidEmail(true);
    }

    setEmail(value);
  };

  const handlePasswordChange = (value: string) => {
    if (isEmptyPassword && value) {
      setIsEmptyPassword(false);
    }

    setPassword(value);
  };

  const {isExtension} = useEnvironmentContext();

  return (
    <div className={css.signInPage}>
      {isExtension && (
        <div className={maincss.senseLogo}>
          <Logo />
        </div>
      )}
      <div className={css.signInSection}>
        {!isExtension && (
          <TitleMedium className={css.contentTitle}>
            {getTranslatedText(t, CONSTANTS.SIGNIN_TITLE)}
          </TitleMedium>
        )}
        <form onSubmit={handleSubmit} className={css.credForm}>
          <div className={css.credSection}>
            <div className={css.credInput}>
              <Input
                label={t('Email', 'Email')}
                placeholder={t('Enter_email', 'Enter email')}
                size={isExtension ? 'small' : 'medium'}
                type="text"
                value={email}
                error={!isValidEmail}
                errorText={getTranslatedText(t, CONSTANTS.INVALID_EMAIL)}
                onBlur={validateUserEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e)}
              />
            </div>
            <div className={css.credInput}>
              <Input
                label={t('Password', 'Password')}
                placeholder={t('Enter_password', 'Enter password')}
                size={isExtension ? 'small' : 'medium'}
                type="password"
                error={isEmptyPassword}
                errorText={getTranslatedText(t, CONSTANTS.EMPTY_PASSWORD)}
                value={password}
                onBlur={() => setIsEmptyPassword(!password)}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e)}
              />
            </div>
          </div>
          <div
            className={classify(css.resetPassword, {
              [css.alignSelfCenter]: isExtension,
            })}
          >
            {isExtension ? (
              <BodySmall className={css.forgotLoginText}>
                {t('Can\'t_log_in', 'Can\'t log in?')}
              </BodySmall>
            ) : (
              <BodyMedium className={css.forgotLoginText}>
                {t('Can\'t_log_in', 'Can\'t log in?')}
              </BodyMedium>
            )}
            {isExtension ? (
              <ButtonTextSmall
                color="clickable"
                className={css.resetLink}
                // @ts-expect-error - TS2769 - No overload matches this call.
                onClick={handleResetPassowrd}
              >
                {t('Reset_password', 'Reset password')}
              </ButtonTextSmall>
            ) : (
              <ButtonTextMedium
                color="clickable"
                className={css.resetLink}
                // @ts-expect-error - TS2769 - No overload matches this call.
                onClick={handleResetPassowrd}
              >
                {t('Reset_password', 'Reset password')}
              </ButtonTextMedium>
            )}
          </div>
          <Button
            size={isExtension ? 'small' : 'medium'}
            type="primary"
            classNames={{wrapper: css.signInBtn}}
            onClick={handleSubmit}
            disabled={signingIn}
          >
            {signingIn
              ? t('Signing_In', 'Signing In')
              : t('Sign_In', 'Sign In')}
          </Button>
        </form>
        {!retry_login_in_mins && signinError && (
          <InContextAlert
            semantic="danger"
            classNames={{wrapper: css.loginError}}
          >
            {/* @ts-expect-error - TS2538 - Type 'null' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type. */}
            {ERROR_CODES[signInErrorText]
              ? // @ts-expect-error - TS2538 - Type 'null' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
                getTranslatedError(t, ERROR_CODES[signInErrorText])
              : // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'. | TS2769 - No overload matches this call.
                t(makeKey(signInErrorText), signInErrorText)}
          </InContextAlert>
        )}
        {!!retry_login_in_mins && signinError && (
          <InContextAlert
            semantic="danger"
            classNames={{wrapper: css.loginError}}
          >
            {t(makeKey(retryLoginInMinsKey), {
              retry_login_in_mins,
              defaultValue: signinError,
            })}
          </InContextAlert>
        )}
      </div>
    </div>
  );
};

export default SignInPage;
